"use client";

import { useEffect, useRef } from "react";

// Stack of close callbacks ordered by registration (last = frontmost).
// ESC only fires the last entry — i.e. the frontmost modal.
const modalStack: Array<{ id: number; close: () => void }> = [];
let nextId = 0;

export function Modal({ open, onClose, width, maxWidth, maxHeight, zIndex, children }: {
  open: boolean;
  onClose: () => void;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  zIndex?: number;
  children: React.ReactNode;
}) {
  const idRef = useRef<number | null>(null);
  // Read the latest onClose through a ref so changing its identity on the
  // parent doesn't tear down + re-push this modal onto the top of the stack
  // (which would make ESC close the wrong modal when another sits above it).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const id = nextId++;
    idRef.current = id;
    modalStack.push({ id, close: () => onCloseRef.current() });

    return () => {
      const idx = modalStack.findIndex((e) => e.id === id);
      if (idx !== -1) modalStack.splice(idx, 1);
      idRef.current = null;
    };
  }, [open]);

  // Single global listener approach: each modal registers, but the keydown
  // handler only fires the topmost entry. We attach per-modal so cleanup is
  // automatic, but stopImmediatePropagation prevents lower modals from acting.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || modalStack.length === 0) return;
      const top = modalStack[modalStack.length - 1];
      if (top.id === idRef.current) {
        e.stopImmediatePropagation();
        top.close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50" style={{ zIndex: zIndex ?? 50 }} onClick={onClose}>
      <div
        className="bg-surface border border-border rounded-lg shadow-lg flex flex-col"
        style={{ width, maxWidth, maxHeight, padding: "1.25rem" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
